/**
 * E1 Parallax Effect
 * A reusable parallax system for images and elements
 * 
 * Simple Usage:
 * 1. Add class "e1-parallax-image" to any image
 * 2. The parent element automatically becomes the container
 * 3. Auto-initializes on page load
 * 
 * Advanced Usage:
 * - Specify custom container with containerSelector option
 * - Use data attributes: data-parallax-image
 */

class E1Parallax {
  constructor(options = {}) {
    this.options = {
      // Selectors
      imageSelector: options.imageSelector || '.e1-parallax-image, [data-parallax-image]',
      containerSelector: options.containerSelector || null, // Auto-detect parent if not specified
      
      // Parallax settings
      intensity: options.intensity || 100,
      scale: options.scale || 1.2,
      
      // Performance settings
      throttleDelay: options.throttleDelay || 16, // ~60fps
      resizeDelay: options.resizeDelay || 100,
      
      // Debug
      debug: options.debug || false
    };
    
    this.parallaxElements = [];
    this.isInitialized = false;
    
    // Bind methods
    this.updateParallax = this.updateParallax.bind(this);
    this.handleScroll = this.throttle(this.updateParallax, this.options.throttleDelay);
    this.handleResize = this.throttle(this.updateParallax, this.options.resizeDelay);
  }
  
  /**
   * Initialize parallax effect
   */
  init() {
    if (this.isInitialized) {
      this.log('Parallax already initialized');
      return;
    }
    
    this.findElements();
    this.setupElements();
    this.bindEvents();
    this.updateParallax();
    
    this.isInitialized = true;
    this.log(`Parallax initialized with ${this.parallaxElements.length} elements`);
  }
  
  /**
   * Find all parallax elements on the page
   */
  findElements() {
    const images = document.querySelectorAll(this.options.imageSelector);
    this.parallaxElements = [];
    
    images.forEach((image, index) => {
      let container;
      
      if (this.options.containerSelector) {
        // Use specified container selector
        container = image.closest(this.options.containerSelector);
      } else {
        // Auto-detect: use parent element as container
        container = image.parentElement;
      }
      
      if (container) {
        this.parallaxElements.push({
          container,
          image,
          index
        });
      } else {
        this.log(`No container found for parallax image ${index}`);
      }
    });
  }
  
  /**
   * Setup initial styles for parallax elements
   */
  setupElements() {
    this.parallaxElements.forEach(({ image, container }) => {
      // Ensure container has overflow hidden
      if (getComputedStyle(container).overflow !== 'hidden') {
        container.style.overflow = 'hidden';
      }
      
      // Set initial transform and transition
      image.style.transform = `scale(${this.options.scale}) translateY(0px)`;
      image.style.transition = 'transform 0.1s ease-out';
      
      // Ensure image covers container
      if (!image.style.objectFit) {
        image.style.objectFit = 'cover';
      }
    });
  }
  
  /**
   * Bind scroll and resize events
   */
  bindEvents() {
    window.addEventListener('scroll', this.handleScroll, { passive: true });
    window.addEventListener('resize', this.handleResize);
  }
  
  /**
   * Update parallax effect for all elements
   */
  updateParallax() {
    const windowHeight = window.innerHeight;
    
    this.parallaxElements.forEach(({ container, image, index }) => {
      const rect = container.getBoundingClientRect();
      
      // Check if element is in viewport (with buffer)
      const buffer = windowHeight * 0.1; // 10% buffer
      const isInViewport = rect.bottom >= -buffer && rect.top <= windowHeight + buffer;
      
      if (isInViewport) {
        // Calculate scroll progress (0 to 1)
        const scrollProgress = (windowHeight - rect.top) / (windowHeight + rect.height);
        
        // Calculate parallax offset
        const parallaxOffset = (scrollProgress - 0.5) * this.options.intensity;
        
        // Apply transform
        const transform = `scale(${this.options.scale}) translateY(${parallaxOffset}px)`;
        image.style.transform = transform;
        
        // Debug first element only
        if (this.options.debug && index === 0) {
          this.log('Parallax update:', {
            scrollProgress: scrollProgress.toFixed(3),
            parallaxOffset: parallaxOffset.toFixed(1),
            transform
          });
        }
      }
    });
  }
  
  /**
   * Destroy parallax effect
   */
  destroy() {
    window.removeEventListener('scroll', this.handleScroll);
    window.removeEventListener('resize', this.handleResize);
    
    // Reset transforms
    this.parallaxElements.forEach(({ image }) => {
      image.style.transform = '';
      image.style.transition = '';
    });
    
    this.parallaxElements = [];
    this.isInitialized = false;
    
    this.log('Parallax destroyed');
  }
  
  /**
   * Update settings and reinitialize
   */
  updateSettings(newOptions) {
    this.destroy();
    Object.assign(this.options, newOptions);
    this.init();
  }
  
  /**
   * Throttle function for performance
   */
  throttle(func, limit) {
    let inThrottle;
    return function(...args) {
      if (!inThrottle) {
        func.apply(this, args);
        inThrottle = true;
        setTimeout(() => inThrottle = false, limit);
      }
    };
  }
  
  /**
   * Debug logging
   */
  log(...args) {
    if (this.options.debug) {
      console.log('[E1Parallax]', ...args);
    }
  }
  
  /**
   * Static method to create and initialize parallax
   */
  static init(options = {}) {
    const parallax = new E1Parallax(options);
    parallax.init();
    return parallax;
  }
}

// Auto-initialize on DOM ready if elements exist
document.addEventListener('DOMContentLoaded', () => {
  const hasParallaxElements = document.querySelector('.e1-parallax-image, [data-parallax-image]');
  
  if (hasParallaxElements) {
    // Auto-initialize with default settings
    window.e1Parallax = E1Parallax.init({
      debug: false // Set to true for debugging
    });
  }
});

// Export for module usage
if (typeof module !== 'undefined' && module.exports) {
  module.exports = E1Parallax;
}

// Global access
window.E1Parallax = E1Parallax;
